import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Shield, Activity, CheckCircle, XCircle, Loader2, Users, Database, Sparkles } from "lucide-react";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";

export default function AdminPage() {
  const { email, groups } = useAuth();
  const [health, setHealth] = useState<{ status: string; version: string; dependencies: Record<string, string> } | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const h = await api.health();
      setHealth(h);
    } catch {
      setHealth(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const StatusIcon = ({ status }: { status: string }) =>
    status === "ok" || status === "healthy" ? <CheckCircle className="text-green-600" size={20} /> : <XCircle className="text-red-600" size={20} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Shield className="text-nhs-green" /> Admin Console</h1>
        <p className="text-sm text-gray-600 mt-1">Platform health, user management, and operational metrics.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold flex items-center gap-2"><Activity size={18} /> System Health</h3>
            <button onClick={refresh} className="text-xs text-nhs-blue hover:underline" disabled={loading}>
              {loading ? "Checking..." : "Refresh"}
            </button>
          </div>
          {health ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Overall</span>
                <div className="flex items-center gap-1"><StatusIcon status={health.status} /> <span>{health.status}</span></div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Version</span>
                <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{health.version}</span>
              </div>
              {Object.entries(health.dependencies).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between text-sm">
                  <span className="capitalize">{key}</span>
                  <div className="flex items-center gap-1"><StatusIcon status={value} /> <span>{value}</span></div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">{loading ? <Loader2 className="animate-spin inline" size={14} /> : "Unable to fetch health"}</p>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 }} className="card">
          <h3 className="font-semibold flex items-center gap-2 mb-3"><Sparkles size={18} /> Model</h3>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span>LLM</span><span className="font-mono text-xs">Claude Opus 4.7</span></div>
            <div className="flex justify-between"><span>Embedding</span><span className="font-mono text-xs">Titan v2 (1024d)</span></div>
            <div className="flex justify-between"><span>Region</span><span>eu-west-2</span></div>
            <div className="flex justify-between"><span>Data residency</span><span>UK only</span></div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="card">
          <h3 className="font-semibold flex items-center gap-2 mb-3"><Users size={18} /> Your session</h3>
          <div className="space-y-1 text-sm">
            <div><span className="text-gray-500">Email:</span> {email}</div>
            <div><span className="text-gray-500">Groups:</span> {groups.length ? groups.join(", ") : "none"}</div>
          </div>
        </motion.div>
      </div>

      <div className="card">
        <h3 className="font-semibold flex items-center gap-2 mb-3"><Database size={18} /> Knowledge Base</h3>
        <div className="grid md:grid-cols-4 gap-3 text-sm">
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-xs text-gray-500 uppercase">Vitamins</div>
            <div className="text-2xl font-bold text-nhs-blue">15</div>
            <div className="text-xs text-gray-600">with UK NHS RDAs</div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-xs text-gray-500 uppercase">NICE Guidelines</div>
            <div className="text-2xl font-bold text-nhs-blue">20</div>
            <div className="text-xs text-gray-600">indexed for RAG</div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-xs text-gray-500 uppercase">Demo Profiles</div>
            <div className="text-2xl font-bold text-nhs-blue">10</div>
            <div className="text-xs text-gray-600">covering UK demographics</div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-xs text-gray-500 uppercase">Rules Engine</div>
            <div className="text-2xl font-bold text-nhs-blue">12</div>
            <div className="text-xs text-gray-600">NHS/SACN/NICE rules</div>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="font-semibold mb-3">Operational Links</h3>
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <a href="https://console.aws.amazon.com/cloudwatch/home?region=eu-west-2#dashboards:name=MedModel-Operations" target="_blank" rel="noopener noreferrer" className="p-3 border border-gray-200 rounded hover:bg-gray-50">
            <div className="font-semibold">CloudWatch Dashboard</div>
            <div className="text-xs text-gray-600">API latency, errors, token usage</div>
          </a>
          <a href="https://console.aws.amazon.com/cognito/v2/idp/user-pools?region=eu-west-2" target="_blank" rel="noopener noreferrer" className="p-3 border border-gray-200 rounded hover:bg-gray-50">
            <div className="font-semibold">Cognito User Pool</div>
            <div className="text-xs text-gray-600">Manage users and groups</div>
          </a>
          <a href="https://console.aws.amazon.com/bedrock/home?region=eu-west-2" target="_blank" rel="noopener noreferrer" className="p-3 border border-gray-200 rounded hover:bg-gray-50">
            <div className="font-semibold">Bedrock Console</div>
            <div className="text-xs text-gray-600">Model access, usage</div>
          </a>
          <a href="https://console.aws.amazon.com/cloudtrail/home?region=eu-west-2" target="_blank" rel="noopener noreferrer" className="p-3 border border-gray-200 rounded hover:bg-gray-50">
            <div className="font-semibold">CloudTrail</div>
            <div className="text-xs text-gray-600">Audit logs (1-year retention)</div>
          </a>
        </div>
      </div>
    </div>
  );
}
